// Custom hooks for analytics/charts API calls
import { useQuery, useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';
import { sortWeeksNewestFirst } from '../../pages/components/charts/terminalPricePulseTableUtils';
import {
  loadChartData,
  loadPriceChart,
  loadTerminalPriceChart,
  loadTerminalPricePulseTable,
  loadDailyVolumeChart,
  loadHistoricVolumeChart,
  loadPredictionsChart,
  loadClusterChart,
  loadProducingRegions,
  loadFobPriceAvg,
  loadFobPriceByOrigin,
  loadFobSeasonality,
  loadMarketShareChart,
  loadMarketShareSummary,
  loadPriceClassifierData,
  classifyPrice,
  loadCargoVarieties,
  loadStandardAnalyticsScorecards,
  loadFobPricePulseScorecards,
  loadVolumeByOriginScorecards,
  loadTerminalPricePulseScorecards,
} from '../api';
import { normalizeFobFilterOptions } from '../../pages/utils/analyticsHelpers';

/** Normalize predictions endpoint payload to a row array (shape varies by backend version). */
const normalizePredictionsResponse = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  const d = payload.data;
  if (Array.isArray(d)) return d;
  if (d && Array.isArray(d.rows)) return d.rows;
  if (Array.isArray(payload.rows)) return payload.rows;
  return [];
};

/**
 * Custom hook to fetch all chart data using react-query
 * Calls load_all_charts_endpoint which returns:
 * - DATA_usa_price_chart
 * - DATA_usa_daily_volume_chart
 * - DATA_usa_historic_volume_chart
 * - DATA_usa_predictions_chart
 * - DATA_usa_cluster_chart
 * 
 * @param {string} email - User email
 * @param {string} commodity - Selected commodity (lowercase)
 * @param {string|null} dateStart - Optional start date (YYYY-MM-DD)
 * @param {string|null} dateEnd - Optional end date (YYYY-MM-DD)
 * @returns {Object} - { chartData, priceChart, volumeChart, historicVolumeChart, predictionsChart, clusterChart, isLoading, isError, error, refetch }
 */
export const useAllChartsData = (email, commodity, dateStart = null, dateEnd = null) => {
  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['allChartsData', email, commodity, dateStart, dateEnd],
    queryFn: () => loadChartData(commodity, dateStart, dateEnd),
    enabled: !!commodity,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // Extract individual chart data from response
  const chartData = data?.data || {};

  return {
    // Raw response
    chartData,
    // Individual chart data for convenience
    priceChart: chartData.DATA_usa_price_chart || [],
    volumeChart: chartData.DATA_usa_daily_volume_chart || [],
    historicVolumeChart: chartData.DATA_usa_historic_volume_chart || [],
    predictionsChart: chartData.DATA_usa_predictions_chart || [],
    clusterChart: chartData.DATA_usa_cluster_chart || [],
    // Query states
    isLoading,
    isError,
    error,
    refetch,
  };
};

// =============================================================================
// Individual Chart Hooks
// =============================================================================

/**
 * Custom hook to fetch USA price chart data
 * Returns price data (low/high) per district with product attributes
 * 
 * @param {string} email - User email
 * @param {string} commodity - Selected commodity (lowercase)
 * @param {string|null} dateStart - Optional start date (YYYY-MM-DD)
 * @param {string|null} dateEnd - Optional end date (YYYY-MM-DD)
 */
export const usePriceChartData = (email, commodity, dateStart = null, dateEnd = null) => {
  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['priceChartData', email, commodity, dateStart, dateEnd],
    queryFn: () => loadPriceChart(commodity, dateStart, dateEnd),
    enabled: !!commodity,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  return {
    priceChart: data?.data || [],
    isLoading,
    isError,
    error,
    refetch,
  };
};

/**
 * Custom hook to fetch USA terminal price chart data
 * Returns price data (low/high) per district with product attributes
 * 
 * @param {string} email - User email
 * @param {string} commodity - Selected commodity (lowercase)
 * @param {string|null} dateStart - Optional start date (YYYY-MM-DD)
 * @param {string|null} dateEnd - Optional end date (YYYY-MM-DD)
 */
export const useTerminalPriceChartData = (email, commodity, dateStart = null, dateEnd = null) => {
  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['terminalPriceChartData', email, commodity, dateStart, dateEnd],
    queryFn: () => loadTerminalPriceChart(commodity, dateStart, dateEnd),
    enabled: !!commodity,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  return {
    priceChart: data?.data || [],
    isLoading,
    isError,
    error,
    refetch,
  };
};

/**
 * Terminal price pulse table — weekly table endpoint (ignores dashboard date range).
 */
export const useTerminalPricePulseTableData = (
  email,
  commodity,
  find = 'all',
  isoWeek = null,
  isoYear = null
) => {
  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ['terminal-price-pulse-table', email, commodity, find, isoWeek, isoYear],
    queryFn: () =>
      loadTerminalPricePulseTable({
        email,
        commodity,
        find,
        isoWeek,
        isoYear,
        weeks: 8,
      }),
    enabled: !!email && !!commodity,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 0,
  });

  return {
    pulseResponse: data || null,
    pulseRows: Array.isArray(data?.data) ? data.data : [],
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  };
};

/**
 * Meta fetch (no iso week) — returns available_weeks for dropdown defaults.
 */
export const useTerminalPricePulseMeta = (email, commodity, find = 'all') => {
  const {
    data,
    isLoading,
    isError,
    error,
    isFetching,
  } = useQuery({
    queryKey: ['terminal-price-pulse-table', 'meta', email, commodity, find],
    queryFn: () =>
      loadTerminalPricePulseTable({
        email,
        commodity,
        find,
        weeks: 8,
      }),
    enabled: !!email && !!commodity,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 0,
  });

  return {
    meta: data || null,
    availableWeeks: sortWeeksNewestFirst(data?.available_weeks || []),
    findLabel: data?.find_label || '',
    isLoading,
    isFetching,
    isError,
    error,
  };
};

/**
 * Fetch table rows for multiple ISO weeks (parallel queries, merged with week metadata).
 */
export const useTerminalPricePulseMultiWeekData = (email, commodity, find, selectedWeeks = []) => {
  const weeks = useMemo(() => selectedWeeks || [], [selectedWeeks]);

  const results = useQueries({
    queries: weeks.map((w) => ({
      queryKey: ['terminal-price-pulse-table', email, commodity, find, w.iso_week, w.iso_year],
      queryFn: () =>
        loadTerminalPricePulseTable({
          email,
          commodity,
          find,
          isoWeek: w.iso_week,
          isoYear: w.iso_year,
          weeks: 8,
        }),
      enabled: !!email && !!commodity && w.iso_week != null && w.iso_year != null,
      staleTime: 2 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      retry: 0,
    })),
  });

  return useMemo(() => {
    const isLoading = results.some((r) => r.isLoading);
    const isFetching = results.some((r) => r.isFetching);
    const isError = results.some((r) => r.isError);
    const error = results.find((r) => r.isError)?.error;
    const pulseRows = results.flatMap((r, i) => {
      const w = weeks[i];
      const rows = Array.isArray(r.data?.data) ? r.data.data : [];
      const label =
        w?.week_label ||
        r.data?.selected_week?.week_label ||
        `W${w?.iso_week} – ${w?.iso_year}`;
      return rows.map((row) => ({
        ...row,
        iso_week: w.iso_week,
        iso_year: w.iso_year,
        week_label: label,
      }));
    });
    return { pulseRows, isLoading, isFetching, isError, error };
  }, [results, weeks]);
};

/**
 * Custom hook to fetch USA daily volume chart data
 * Returns daily volume data with year-over-year comparison
 * 
 * @param {string} email - User email
 * @param {string} commodity - Selected commodity (lowercase)
 * @param {string|null} dateStart - Optional start date (YYYY-MM-DD)
 * @param {string|null} dateEnd - Optional end date (YYYY-MM-DD)
 */
export const useDailyVolumeChartData = (email, commodity, dateStart = null, dateEnd = null, options = {}) => {
  const { enabled: enabledOption = true } = options;
  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['dailyVolumeChartData', email, commodity, dateStart, dateEnd],
    queryFn: () => loadDailyVolumeChart(commodity, dateStart, dateEnd),
    enabled: !!commodity && enabledOption,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  return {
    volumeChart: (() => {
      const payload = data;
      if (Array.isArray(payload)) return payload;
      if (Array.isArray(payload?.data)) return payload.data;
      if (Array.isArray(payload?.response)) return payload.response;
      if (Array.isArray(payload?.rows)) return payload.rows;
      return [];
    })(),
    isLoading,
    isError,
    error,
    refetch,
  };
};

/**
 * Custom hook to fetch USA historic volume chart data
 * Returns historical volume data by ISO week for season comparison
 * Note: This endpoint does not support date parameters
 * 
 * @param {string} email - User email
 * @param {string} commodity - Selected commodity (lowercase)
 */
export const useHistoricVolumeChartData = (email, commodity) => {
  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['historicVolumeChartData', email, commodity],
    queryFn: () => loadHistoricVolumeChart(commodity),
    enabled: !!commodity,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  return {
    historicVolumeChart: data?.data || [],
    isLoading,
    isError,
    error,
    refetch,
  };
};

/**
 * Custom hook to fetch USA predictions chart data
 * Returns season prediction and progress data
 * 
 * @param {string} email - User email
 * @param {string} commodity - Selected commodity (lowercase)
 * @param {string|null} dateStart - Optional start date (YYYY-MM-DD), used if dateEnd omitted (withChartData order)
 * @param {string|null} dateEnd - Optional end date (YYYY-MM-DD) — sent as date_end to API
 */
export const usePredictionsChartData = (email, commodity, dateStart = null, dateEnd = null) => {
  const dateForApi = dateEnd || dateStart || null;

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['predictionsChartData', email, commodity, dateForApi],
    queryFn: () => loadPredictionsChart(commodity, dateForApi),
    enabled: !!commodity,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  return {
    predictionsChart: normalizePredictionsResponse(data),
    isLoading,
    isError,
    error,
    refetch,
  };
};

/**
 * Custom hook to fetch USA cluster chart data
 * Returns ML-clustered weekly price data with price categories
 * Note: This endpoint does not support date parameters
 * 
 * @param {string} email - User email
 * @param {string} commodity - Selected commodity (lowercase)
 */
export const useClusterChartData = (email, commodity) => {
  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['clusterChartData', email, commodity],
    queryFn: () => loadClusterChart(commodity),
    enabled: !!commodity,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  return {
    clusterChart: data?.data || [],
    isLoading,
    isError,
    error,
    refetch,
  };
};

/**
 * Custom hook to fetch producing regions map data
 * Returns global producing regions with harvest status and varieties
 * Supports date_end parameter to determine harvest status based on a specific month
 * Region data is relatively stable, so we use longer cache times
 *
 * @param {string} email - User email
 * @param {string} commodity - Selected commodity (lowercase)
 * @param {string|null} dateStart - Not used by this endpoint (included for HOC compatibility)
 * @param {string|null} dateEnd - Optional end date (YYYY-MM-DD) to determine harvest status month
 */
export const useProducingRegionsData = (email, commodity, dateStart = null, dateEnd = null) => {
  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['producingRegionsData', email, commodity, dateEnd],
    queryFn: () => loadProducingRegions(commodity, dateEnd),
    enabled: !!commodity,
    staleTime: 10 * 60 * 1000, // 10 minutes (region data is stable)
    gcTime: 30 * 60 * 1000, // 30 minutes
  });

  return {
    producingRegions: data?.data || [],
    isLoading,
    isError,
    error,
    refetch,
  };
};

/**
 * Custom hook that combines all 5 individual chart hooks
 * Each chart is fetched independently via its own endpoint
 * This provides fine-grained loading states and error handling per chart
 * 
 * @param {string} email - User email
 * @param {string} commodity - Selected commodity (lowercase)
 * @param {string|null} dateStart - Optional start date (YYYY-MM-DD)
 * @param {string|null} dateEnd - Optional end date (YYYY-MM-DD)
 */
export const useIndividualChartsData = (email, commodity, dateStart = null, dateEnd = null) => {
  // Price chart: supports date_start and date_end
  const priceResult = usePriceChartData(email, commodity, dateStart, dateEnd);
  
  // Daily volume chart: supports date_start and date_end
  const volumeResult = useDailyVolumeChartData(email, commodity, dateStart, dateEnd);
  
  // Historic volume chart: does NOT support date parameters
  const historicVolumeResult = useHistoricVolumeChartData(email, commodity);
  
  // Predictions chart: date_end (prefer end date, else start — matches withChartData)
  const predictionsResult = usePredictionsChartData(email, commodity, dateStart, dateEnd);
  
  // Cluster chart: does NOT support date parameters
  const clusterResult = useClusterChartData(email, commodity);

  // Combine loading states
  const isLoading = 
    priceResult.isLoading ||
    volumeResult.isLoading ||
    historicVolumeResult.isLoading ||
    predictionsResult.isLoading ||
    clusterResult.isLoading;

  // Combine error states - return first error encountered
  const isError = 
    priceResult.isError ||
    volumeResult.isError ||
    historicVolumeResult.isError ||
    predictionsResult.isError ||
    clusterResult.isError;

  const error = 
    priceResult.error ||
    volumeResult.error ||
    historicVolumeResult.error ||
    predictionsResult.error ||
    clusterResult.error;

  return {
    // Individual chart data
    priceChart: priceResult.priceChart,
    volumeChart: volumeResult.volumeChart,
    historicVolumeChart: historicVolumeResult.historicVolumeChart,
    predictionsChart: predictionsResult.predictionsChart,
    clusterChart: clusterResult.clusterChart,
    // Combined states
    isLoading,
    isError,
    error,
    // Individual loading states for granular UI feedback
    loadingStates: {
      priceLoading: priceResult.isLoading,
      volumeLoading: volumeResult.isLoading,
      historicVolumeLoading: historicVolumeResult.isLoading,
      predictionsLoading: predictionsResult.isLoading,
      clusterLoading: clusterResult.isLoading,
    },
    // Individual error states
    errorStates: {
      priceError: priceResult.error,
      volumeError: volumeResult.error,
      historicVolumeError: historicVolumeResult.error,
      predictionsError: predictionsResult.error,
      clusterError: clusterResult.error,
    },
    // Refetch functions
    refetch: {
      priceRefetch: priceResult.refetch,
      volumeRefetch: volumeResult.refetch,
      historicVolumeRefetch: historicVolumeResult.refetch,
      predictionsRefetch: predictionsResult.refetch,
      clusterRefetch: clusterResult.refetch,
    },
  };
};

// =============================================================================
// FOB Price Dashboard Hooks
// =============================================================================

/**
 * Hook to fetch FOB average price data for year-over-year comparison
 * Returns last 5 years of monthly data. Frontend handles filtering.
 * Compatible with withChartData HOC signature
 * @param {string} email - User email
 * @param {string} commodity - Commodity name
 */
export const useFobPriceAvgData = (email, commodity) => {
  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['fob-price-avg', email, commodity],
    queryFn: () => loadFobPriceAvg(commodity),
    enabled: !!commodity,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const rawFo = data?.filter_options ?? data?.filterOptions;

  const rows = data?.data || [];

  return {
    // Raw data with: report_date, year, month, iso_week, district, low_price, high_price, avg_price
    fobPriceAvg: rows,
    data: rows,
    years: data?.years || [],
    origins: data?.origins || [],
    filterOptions: normalizeFobFilterOptions(rawFo),
    isLoading,
    isError,
    error,
    refetch,
  };
};

// =============================================================================
// Market Share Dashboard Hooks
// =============================================================================

/**
 * Hook to fetch market share distribution data
 * Returns last 5 years of monthly data. Frontend handles filtering.
 * Compatible with withChartData HOC signature
 * @param {string} email - User email
 * @param {string} commodity - Commodity name
 */
export const useMarketShareChartData = (email, commodity) => {
  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['marketShareChart', email, commodity],
    queryFn: () => loadMarketShareChart(commodity),
    enabled: !!commodity,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  return {
    marketShareData: data?.data || [],
    origins: data?.origins || [],
    years: data?.years || [],
    granularity: data?.granularity || 'monthly',
    isLoading,
    isError,
    error,
    refetch,
  };
};

/**
 * Hook to fetch market share summary table data
 * Returns summary for current period with delta vs last year and 3-yr avg.
 * Compatible with withChartData HOC signature
 * @param {string} email - User email
 * @param {string} commodity - Commodity name
 */
export const useMarketShareSummaryData = (email, commodity) => {
  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['marketShareSummary', email, commodity],
    queryFn: () => loadMarketShareSummary(commodity),
    enabled: !!commodity,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  return {
    summaryData: data?.data || [],
    currentPeriod: data?.current_period || {},
    granularity: data?.granularity || 'monthly',
    isLoading,
    isError,
    error,
    refetch,
  };
};

/**
 * Hook to fetch FOB price by origin data for heatmap and line charts
 * Returns last 5 years of monthly data. Frontend handles filtering.
 * Compatible with withChartData HOC signature
 * @param {string} email - User email
 * @param {string} commodity - Commodity name
 */
export const useFobPriceByOriginData = (email, commodity) => {
  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['fobPriceByOrigin', email, commodity],
    queryFn: () => loadFobPriceByOrigin(commodity),
    enabled: !!commodity,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const rawFo = data?.filter_options ?? data?.filterOptions;

  return {
    fobPriceByOrigin: data?.data || [],
    threeYrAvg: data?.three_yr_avg || [],
    origins: data?.origins || [],
    years: data?.years || [],
    granularity: data?.granularity || 'monthly',
    filterOptions: normalizeFobFilterOptions(rawFo),
    isLoading,
    isError,
    error,
    refetch,
  };
};

/**
 * Hook to fetch seasonality data showing when each origin is in season
 * Returns season availability by month for each origin.
 * Compatible with withChartData HOC signature
 * @param {string} email - User email
 * @param {string} commodity - Commodity name
 */
export const useFobSeasonalityData = (email, commodity) => {
  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['fobSeasonality', email, commodity],
    queryFn: () => loadFobSeasonality(commodity),
    enabled: !!commodity,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  return {
    // Raw volume data for seasonality calculation
    data: data?.data || [],
    origins: data?.origins || [],
    yearsAnalyzed: data?.years_analyzed || [],
    isLoading,
    isError,
    error,
    refetch,
  };
};

// =============================================================================
// Multi-Commodity Regions Hook
// =============================================================================

/**
 * Hook to fetch producing regions for multiple commodities in parallel
 * Returns combined regions from all selected commodities with commodity tags
 *
 * @param {string} email - User email
 * @param {string[]} commodities - Array of commodity names (lowercase)
 * @param {string|null} dateEnd - Optional end date (YYYY-MM-DD) to determine harvest status
 */
export const useMultiCommodityRegionsData = (email, commodities = [], dateEnd = null) => {
  const queries = useQueries({
    queries: commodities.map((commodity) => ({
      queryKey: ['producingRegionsData', email, commodity, dateEnd],
      queryFn: () => loadProducingRegions(commodity, dateEnd),
      enabled: !!commodity,
      staleTime: 10 * 60 * 1000, // 10 minutes - region data is stable
      gcTime: 30 * 60 * 1000, // 30 minutes
    })),
  });

  // Combine results, add commodity tag to each region.
  // NOTE: do NOT pre-filter queries before flatMap — that breaks the index alignment
  // with `commodities[i]`, causing rows to be tagged with the wrong commodityKey
  // when any single query is still loading or errored. Tagging is only correct when
  // `i` indexes the original `queries`/`commodities` arrays.
  const combinedData = useMemo(() => {
    return queries.flatMap((q, i) => {
      const rows = q.data?.data;
      if (!Array.isArray(rows)) return [];
      return rows.map((r) => ({
        ...r,
        commodityKey: commodities[i],
      }));
    });
  }, [queries, commodities]);

  // Calculate loading and error states
  const isLoading = queries.some((q) => q.isLoading);
  const isError = queries.some((q) => q.isError);
  const error = queries.find((q) => q.error)?.error || null;

  return {
    regions: combinedData,
    isLoading,
    isError,
    error,
    // Individual query states for debugging
    queryStates: queries.map((q, i) => ({
      commodity: commodities[i],
      isLoading: q.isLoading,
      isError: q.isError,
      error: q.error,
      regionCount: q.data?.data?.length || 0,
    })),
  };
};

// =============================================================================
// Smart Price Classifier Hooks
// =============================================================================

/**
 * Hook to fetch price classifier filter data (raw rows for cascading filters)
 * @param {string} email - User email
 * @param {string} commodity - Commodity name
 */
export const usePriceClassifierData = (email, commodity) => {
  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['priceClassifierData', email, commodity],
    queryFn: () => loadPriceClassifierData(commodity),
    enabled: !!commodity,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  return {
    data: data?.data || [],
    isLoading,
    isError,
    error,
    refetch,
  };
};

/**
 * Hook for price classification — manual trigger via refetch()
 * Returns 201 pre-computed price points for instant slider reclassification
 * @param {string} email - User email
 * @param {string} commodity - Commodity name
 * @param {number} isoWeek - ISO week number
 * @param {number} isoYear - ISO year
 * @param {Object} filters - Filter selections
 * @param {number} price - Price to classify
 */
export const usePriceClassifierClassify = (email, commodity, isoWeek, isoYear, filters, price) => {
  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['priceClassifierClassify', email, commodity, isoWeek, isoYear, filters, price],
    queryFn: () => classifyPrice(commodity, isoWeek, isoYear, price, filters),
    enabled: false,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  return {
    classifyResult: data?.data || data || null,
    isClassifying: isLoading || isFetching,
    isError,
    error,
    classify: refetch,
  };
};

// =============================================================================
// Cargo Varieties Hook (Peru — butterfly chart)
// =============================================================================

/**
 * Hook to fetch Peru cargo varieties data.
 * Returns all 3 periods (3d, 7d, 10d) in a single API call.
 * The chart selects which period to display via local state.
 *
 * @param {string} email - User email
 * @param {string} commodity - Commodity name (e.g. "grapes")
 * @param {string|null} dateEnd - Optional end date (YYYY-MM-DD)
 * @param {number|null} topN - Optional top N varieties
 */
export const useCargoVarietiesData = (email, commodity, dateEnd = null, topN = null) => {
  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['cargoVarieties', email, commodity, dateEnd, topN],
    queryFn: () => loadCargoVarieties(commodity, dateEnd, topN),
    enabled: !!commodity,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  return {
    available: data?.available,
    availableCommodities: data?.available_commodities || [],
    unavailableMessage: data?.message || '',
    periods: data?.periods || null,
    metadata: data?.metadata || {},
    isLoading,
    isError,
    error,
    refetch,
  };
};

// =============================================================================
// Preset Analytics View Scorecards
// =============================================================================

/**
 * Standard preset view scorecards (3 cards).
 * Backend endpoint will be added separately; this hook is "fail open":
 * if request errors, the UI can still render charts.
 */
export const useStandardAnalyticsScorecards = (apiParams) => {
  const email = apiParams?.email;
  const commodity = apiParams?.commodity;
  const dateStart = apiParams?.dateStart || null;
  const dateEnd = apiParams?.dateEnd || null;

  const query = useQuery({
    queryKey: ['standardAnalyticsScorecards', email, commodity, dateStart, dateEnd],
    queryFn: async () => {
      const resp = await loadStandardAnalyticsScorecards(email, commodity, dateStart, dateEnd);
      return resp?.data || resp || null;
    },
    enabled: !!email && !!commodity,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 0,
  });

  return query;
};

export const useFobPricePulseScorecards = (apiParams) => {
  const email = apiParams?.email;
  const commodity = apiParams?.commodity;
  const dateStart = apiParams?.dateStart || null;
  const dateEnd = apiParams?.dateEnd || null;

  return useQuery({
    queryKey: ['fobPricePulseScorecards', email, commodity, dateStart, dateEnd],
    queryFn: async () => {
      const resp = await loadFobPricePulseScorecards(email, commodity, dateStart, dateEnd);
      return resp?.data || resp || null;
    },
    enabled: !!email && !!commodity,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 0,
  });
};

export const useVolumeByOriginScorecards = (apiParams) => {
  const email = apiParams?.email;
  const commodity = apiParams?.commodity;
  const dateStart = apiParams?.dateStart || null;
  const dateEnd = apiParams?.dateEnd || null;

  return useQuery({
    queryKey: ['volumeByOriginScorecards', email, commodity, dateStart, dateEnd],
    queryFn: async () => {
      const resp = await loadVolumeByOriginScorecards(email, commodity, dateStart, dateEnd);
      return resp?.data || resp || null;
    },
    enabled: !!email && !!commodity,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 0,
  });
};

export const useTerminalPricePulseScorecards = (apiParams) => {
  const email = apiParams?.email;
  const commodity = apiParams?.commodity;
  const dateStart = apiParams?.dateStart || null;
  const dateEnd = apiParams?.dateEnd || null;

  return useQuery({
    queryKey: ['terminalPricePulseScorecards', email, commodity, dateStart, dateEnd],
    queryFn: async () => {
      const resp = await loadTerminalPricePulseScorecards(email, commodity, dateStart, dateEnd);
      return resp?.data || resp || null;
    },
    enabled: !!email && !!commodity,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 0,
  });
};
